import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { ProjectProvider } from "@/context/ProjectContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import { LoginForm, RegisterForm } from "@/pages/AuthPages";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import AuditPage from "@/pages/AuditPage";
import BlogPage from "@/pages/BlogPage";
import KeywordsPage from "@/pages/KeywordsPage";
import BacklinksPage from "@/pages/BacklinksPage";
import ToolsPage from "@/pages/ToolsPage";
import AdminPage from "@/pages/AdminPage";
import OnboardingPage from "@/pages/OnboardingPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ContentPlanPage from "@/pages/ContentPlanPage";
import LLMVisibilityPage from "@/pages/LLMVisibilityPage";
import AutopilotPage from "@/pages/AutopilotPage";
import SocialChannelsPage from "@/pages/SocialChannelsPage";
import FixKitPage from "@/pages/FixKitPage";
import SchedulerPage from "@/pages/SchedulerPage";
import AccountPage from "@/pages/AccountPage";
import { PrivacyPage, TermsPage } from "@/pages/LegalPages";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/ResetPasswordPages";
import AboutPage from "@/pages/AboutPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import OttoPage from "@/pages/OttoPage";
import ExplorerPage from "@/pages/ExplorerPage";
import UpgradePage from "@/pages/UpgradePage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import GoogleAdsPage from "@/pages/GoogleAdsPage";
import PpcLeakPage from "@/pages/PpcLeakPage";
import LocalSeoPage from "@/pages/LocalSeoPage";
import WhitelabelPage from "@/pages/WhitelabelPage";
import AffiliatePage from "@/pages/AffiliatePage";
import PublicPortalPage from "@/pages/PublicPortalPage";
import FreeGoogleAdsAuditPage from "@/pages/FreeGoogleAdsAuditPage";
import ConsentBanner from "@/components/ConsentBanner";
import ChatbotWidget from "@/components/ChatbotWidget";

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Process OAuth callback synchronously during render to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<LoginForm />} />
      <Route path="/register" element={<RegisterForm />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/free-google-ads-audit" element={<FreeGoogleAdsAuditPage />} />
      <Route path="/p/:slug/c/:token" element={<PublicPortalPage />} />
      <Route path="/p/*" element={<PublicPortalPage />} />

      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute allowExpired><AccountPage /></ProtectedRoute>} />
      <Route path="/upgrade" element={<ProtectedRoute allowExpired><UpgradePage /></ProtectedRoute>} />
      <Route path="/billing/success" element={<ProtectedRoute allowExpired><BillingSuccessPage /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/autopilot" element={<ProtectedRoute><AutopilotPage /></ProtectedRoute>} />
      <Route path="/scheduler" element={<ProtectedRoute><SchedulerPage /></ProtectedRoute>} />
      <Route path="/social" element={<ProtectedRoute><SocialChannelsPage /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
      <Route path="/otto" element={<ProtectedRoute><OttoPage /></ProtectedRoute>} />
      <Route path="/explorer" element={<ProtectedRoute><ExplorerPage /></ProtectedRoute>} />
      <Route path="/fix-kit" element={<ProtectedRoute><FixKitPage /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
      <Route path="/content-plan" element={<ProtectedRoute><ContentPlanPage /></ProtectedRoute>} />
      <Route path="/llm-visibility" element={<ProtectedRoute><LLMVisibilityPage /></ProtectedRoute>} />
      <Route path="/audit" element={<ProtectedRoute><AuditPage /></ProtectedRoute>} />
      <Route path="/blog" element={<ProtectedRoute><BlogPage /></ProtectedRoute>} />
      <Route path="/keywords" element={<ProtectedRoute><KeywordsPage /></ProtectedRoute>} />
      <Route path="/google-ads" element={<ProtectedRoute><GoogleAdsPage /></ProtectedRoute>} />
      <Route path="/ppc-leak" element={<ProtectedRoute><PpcLeakPage /></ProtectedRoute>} />
      <Route path="/local-seo" element={<ProtectedRoute><LocalSeoPage /></ProtectedRoute>} />
      <Route path="/whitelabel" element={<ProtectedRoute allowExpired><WhitelabelPage /></ProtectedRoute>} />
      <Route path="/affiliate" element={<ProtectedRoute allowExpired><AffiliatePage /></ProtectedRoute>} />
      <Route path="/backlinks" element={<ProtectedRoute><BacklinksPage /></ProtectedRoute>} />
      <Route path="/tools" element={<ProtectedRoute><ToolsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminPage /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ProjectProvider>
            <AppRouter />
            <ConsentBanner />
            <ChatbotWidget />
            <Toaster theme="dark" />
          </ProjectProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
